import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useUserProfile } from '@/contexts/profile/ProfileContext';
import { useSubscriptionStatus } from "@/hooks/subscription/useSubscriptionStatus";
import { createServiceProvider, updateServiceProvider } from "@/services/providers/providerOwnerService";
import { getCategories } from "@/utils/supabase/categoryHelpers";
import { getAreasGroupedByTerritory } from "@/utils/supabase/areaHelpers";
import { ServiceProviderRow, AreaResult } from "@/types/supabase";
import type { ListingEditableFields } from '@/types/domain';
import { debugLogger } from '@/utils/debug';

import BasicInfoSection from "./listing-form/BasicInfoSection";
import LocationSection from "./listing-form/LocationSection";
import MediaUploadSection from "./listing-form/MediaUploadSection";
import TermsSection from "./listing-form/TermsSection";
import FormActions from "./listing-form/FormActions";
import type { ListingFormValues } from "./listing-form/types";
import { mergeUniqueSelections, removeSelections } from "@/utils/areaSelection";
import { useAreaDisplay } from "@/hooks/useAreaDisplay";
import { AlertCircle } from "lucide-react";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const formSchema = z.object({
  name: z.string().min(2, "Service name must be at least 2 characters"),
  category: z.string().min(1, "Category is required"),
  categories: z.array(z.string()).min(1, "At least one category is required"),
  // Allow users to enter domains without protocol (example.com) and empty string.
  // We validate by attempting to construct a URL, adding https:// when protocol is missing.
  website_url: z.string().optional().or(z.literal("")).refine((val) => {
    if (!val) return true;
    try {
      // If user omitted protocol, try with https://
      const candidate = val.startsWith("http://") || val.startsWith("https://") ? val : `https://${val}`;
      // Will throw if invalid
      new URL(candidate);
      return true;
    } catch {
      return false;
    }
  }, "Please enter a valid URL (e.g., example.com or https://example.com)"),
  description: z.string().min(20, "Description must be at least 20 characters").max(2000, "Description must be less than 2000 characters"),
  suburbs: z.array(z.string()).min(1, "Please select at least one area"),
  image: z.any().optional(),
  terms_accepted: z.boolean().default(false)
});
type FormValues = z.infer<typeof formSchema>;
type ListingFormStepId = "service" | "visibility" | "image" | "review";

const listingFormSteps: Array<{ id: ListingFormStepId; title: string; description: string }> = [
  {
    id: "service",
    title: "Service details",
    description: "Name, description, and contact details",
  },
  {
    id: "visibility",
    title: "Visibility",
    description: "Categories and areas served",
  },
  {
    id: "image",
    title: "Image",
    description: "Choose the listing cover image",
  },
  {
    id: "review",
    title: "Review",
    description: "Preview and submit for review",
  },
];

interface ListingFormProps {
  isDisabled?: boolean;
  onSubmitSuccess?: () => void;
  editMode?: boolean;
  initialData?: ServiceProviderRow | null;
  isPremium?: boolean;
  isAdmin?: boolean;
  onCancel?: () => void;
  reviewNotice?: string;
}

const ListingForm = ({
  isDisabled = false,
  onSubmitSuccess,
  editMode = false,
  initialData = null,
  isPremium = false,
  isAdmin = false,
  onCancel,
  reviewNotice,
}: ListingFormProps) => {
  const {
    user
  } = useAuth();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [selectedSuburbs, setSelectedSuburbs] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [useDefaultBanner, setUseDefaultBanner] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  
  const {
    data: categories = []
  } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories
  });
  const {
    data: areasGrouped = {}
  } = useQuery({
    queryKey: ["areasGrouped"],
    queryFn: getAreasGroupedByTerritory
  });
  
  // Get subscription status using the proper hook
  const { data: subscriptionData } = useSubscriptionStatus();
  const isWhatsAppTier = subscriptionData?.tier === 'whatsapp_tier';
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialData?.name || "",
      category: initialData?.category || "",
      categories: initialData?.categories || [],
      website_url: initialData?.website_url || "",
      description: initialData?.description || "",
      suburbs: initialData?.suburbs || [],
      terms_accepted: false
    }
  });
  
  // Unified current user profile
  const { profile: userProfile } = useUserProfile();
  const isWhatsAppVerified = userProfile?.whatsapp_verified || false;
  
  useEffect(() => {
    if (editMode && initialData) {
      form.reset({
        name: initialData.name || "",
        category: initialData.category || "",
        categories: initialData.categories || [],
        website_url: initialData.website_url || "",
        description: initialData.description || "",
        suburbs: initialData.suburbs || [],
        terms_accepted: false
      });
      if (initialData.image_url) {
        // Check if using default banner
        if (initialData.image_url === "/default-banner.png") {
          setUseDefaultBanner(true);
        } else {
          setImagePreview(initialData.image_url);
        }
      }
      if (initialData.suburbs && initialData.suburbs.length > 0) {
        setSelectedSuburbs(initialData.suburbs);
      }
      if (initialData.categories && initialData.categories.length > 0) {
        setSelectedCategories(initialData.categories);
      }
    }
  }, [editMode, initialData, form]);
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setImageError(null);
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setImageError("Image must be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        setImageError("File must be an image");
        return;
      }
      setFileName(file.name);
      form.setValue("image", file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUseDefaultBannerChange = (checked: boolean) => {
    setUseDefaultBanner(checked);
    if (checked) {
      // Clear any uploaded image
      setImagePreview(null);
      setFileName(null);
      setImageError(null);
      form.setValue("image", undefined);
    }
  };

  const getMaxSuburbsAllowed = () => {
    if (isPremium || isAdmin) return Infinity;
    if (isWhatsAppVerified) return 4;
    return 2;
  };

  const updateSelectedSuburbs = (nextSuburbs: string[]) => {
    setSelectedSuburbs(nextSuburbs);
    form.setValue("suburbs", nextSuburbs);
  };

  const handleSuburbBulkSelect = (suburbsToAdd: string[]) => {
    const uniqueNewSuburbs = suburbsToAdd.filter((suburb) => !selectedSuburbs.includes(suburb));
    if (uniqueNewSuburbs.length === 0) {
      return;
    }

    const maxSuburbsAllowed = getMaxSuburbsAllowed();
    const remainingCapacity = maxSuburbsAllowed === Infinity
      ? Infinity
      : Math.max(maxSuburbsAllowed - selectedSuburbs.length, 0);

    if (remainingCapacity === 0) {
      return;
    }

    const suburbsWithinLimit = remainingCapacity === Infinity
      ? uniqueNewSuburbs
      : uniqueNewSuburbs.slice(0, remainingCapacity);

    updateSelectedSuburbs(mergeUniqueSelections(selectedSuburbs, suburbsWithinLimit));
  };

  const handleSuburbBulkRemove = (suburbsToRemove: string[]) => {
    updateSelectedSuburbs(removeSelections(selectedSuburbs, suburbsToRemove));
  };
  
  // Modified to handle suburb selection with consistent limits
  const handleSuburbSelect = (suburb: string) => {
    handleSuburbBulkSelect([suburb]);
  };
  
  const handleRemoveSuburb = (suburb: string) => {
    handleSuburbBulkRemove([suburb]);
  };
  
  const handleCategorySelect = (category: string) => {
    if (!selectedCategories.includes(category)) {
      if (!isPremium && !isWhatsAppVerified && selectedCategories.length >= 1) {
        return;
      }
      
      if (isWhatsAppVerified && !isPremium && selectedCategories.length >= 1) {
        return;
      }
      
      const updatedCategories = [...selectedCategories, category];
      setSelectedCategories(updatedCategories);
      form.setValue("categories", updatedCategories);
      
      // Ensure the primary category is set if it's the first one selected
      if (updatedCategories.length === 1) {
        form.setValue("category", category);
      }
    }
  };
  
  const handleRemoveCategory = (category: string) => {
    const updatedCategories = selectedCategories.filter(c => c !== category);
    setSelectedCategories(updatedCategories);
    form.setValue("categories", updatedCategories);
    if (form.getValues("category") === category && updatedCategories.length > 0) {
      form.setValue("category", updatedCategories[0]);
    }
  };

  const currentStep = listingFormSteps[currentStepIndex];
  const watchedValues = form.watch();
  const { areaLabels } = useAreaDisplay(selectedSuburbs);
  const reviewImageSrc = useDefaultBanner
    ? "/default-banner.png"
    : imagePreview || initialData?.image_url || null;
  const canGoBack = currentStepIndex > 0;
  const isReviewStep = currentStep.id === "review";

  const validateImageStep = () => {
    const values = form.getValues();
    const hasExistingImage = editMode && Boolean(initialData?.image_url);
    const hasNewImage = values.image instanceof File;

    if (!hasExistingImage && !hasNewImage && !useDefaultBanner) {
      setImageError("Please upload an image or use the default banner");
      return false;
    }

    setImageError(null);
    return true;
  };

  const validateCurrentStep = async () => {
    if (currentStep.id === "service") {
      return form.trigger(["name", "description", "website_url"]);
    }

    if (currentStep.id === "visibility") {
      const isValid = await form.trigger(["category", "categories", "suburbs"]);

      if (selectedCategories.length === 0) {
        form.setError("categories", {
          message: "Please select at least one category",
          type: "manual",
        });
        return false;
      }

      form.clearErrors("categories");
      return isValid;
    }

    if (currentStep.id === "image") {
      return validateImageStep();
    }

    return true;
  };

  const handleNextStep = async () => {
    const isValid = await validateCurrentStep();

    if (!isValid) {
      return;
    }

    setCurrentStepIndex((current) => Math.min(current + 1, listingFormSteps.length - 1));
  };

  const handlePreviousStep = () => {
    setCurrentStepIndex((current) => Math.max(current - 1, 0));
  };
  
  const onSubmit = async (values: FormValues) => {
    if (isDisabled) return;
    try {
      setIsSubmitting(true);
      setTermsError(null);
      
      if (!validateImageStep()) {
        setIsSubmitting(false);
        return;
      }
      
      if (!values.terms_accepted) {
        setTermsError("Please accept the Terms and Conditions to continue");
        setIsSubmitting(false);
        return;
      }
      
      if (selectedCategories.length === 0) {
        // Consistent with inline error messaging approach
        form.setError("categories", { 
          message: "Please select at least one category" 
        });
        setIsSubmitting(false);
        return;
      }
      
      // Normalize website URL: if user omitted protocol, prepend https://
      let normalizedWebsiteUrl: string | null = null;
      if (values.website_url && values.website_url !== "") {
        const trimmed = values.website_url.trim();
        normalizedWebsiteUrl = trimmed.startsWith("http://") || trimmed.startsWith("https://")
          ? trimmed
          : `https://${trimmed}`;
      }

      const data: ListingEditableFields & { image_url: string } = {
        name: values.name,
        category: values.category,
        categories: selectedCategories,
        website_url: normalizedWebsiteUrl || null,
        description: values.description,
        suburbs: values.suburbs,
        image_url: initialData?.image_url || "/default-banner.png"
      };
      
      let imageUrl = initialData?.image_url;
      
      if (useDefaultBanner) {
        imageUrl = "/default-banner.png";
      } else if (values.image && values.image instanceof File) {
        debugLogger.log("Uploading image...");
        const imagePath = `service_images/${user?.id}/${Date.now()}_${values.image.name}`;
        // Dynamically import uploadFileToStorage to avoid static/dynamic import conflict
        const { uploadFileToStorage } = await import("@/utils/supabase/storageHelpers");
        const uploadResult = await uploadFileToStorage("service-provider-assets", imagePath, values.image);
        imageUrl = uploadResult || undefined;
        if (!imageUrl) {
          setImageError("Error uploading image. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }
      data.image_url = imageUrl || "/default-banner.png"; // ensure required
      
      let result;
      if (editMode && initialData?.id) {
        debugLogger.log("Updating listing:", initialData.id);
        debugLogger.log("Update data:", data);
        
        try {
          result = await updateServiceProvider(initialData.id, data);
          toast.success("Service listing updated successfully!");
        } catch (error: unknown) {
          console.error("Error updating service listing:", error);
          throw error;
        }
      } else {
        debugLogger.log("Creating new listing");
        debugLogger.log("Insert data:", data);
        
        try {
          if (!user?.id) {
            throw new Error("User not authenticated");
          }
          
          result = await createServiceProvider(data);
          toast.success("Service listing submitted successfully!");
        } catch (error: unknown) {
          console.error("Error submitting service listing:", error);
          throw error;
        }
      }
      
      debugLogger.log("Operation result:", result);
      form.reset();
      setImagePreview(null);
      setFileName(null);
      setSelectedSuburbs([]);
      setSelectedCategories([]);
      setUseDefaultBanner(false);
      setCurrentStepIndex(0);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userListings'] }),
        queryClient.invalidateQueries({ queryKey: ['userListingsCount'] }),
        queryClient.invalidateQueries({ queryKey: ['mobileUserListingsCount'] }),
        queryClient.invalidateQueries({ queryKey: ['mobileUserApprovedListings'] }),
        queryClient.invalidateQueries({ queryKey: ['ctaUserListingsCount'] }),
      ]);
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (error: unknown) {
      console.error("Error submitting service listing:", error);
      const message = error instanceof Error ? error.message : String(error);
      toast.error(`Failed to ${editMode ? "update" : "submit"} listing: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const finalReviewNotice = reviewNotice || (editMode
    ? "Your listing changes will be reviewed before going live."
    : "Your listing will be reviewed before going live.");

  return <div className="space-y-4">
    <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {listingFormSteps.map((step, index) => {
        const isActive = index === currentStepIndex;
        const isComplete = index < currentStepIndex;

        return (
          <li key={step.id}>
            <button
              type="button"
              onClick={() => {
                if (index < currentStepIndex) {
                  setCurrentStepIndex(index);
                }
              }}
              disabled={index > currentStepIndex}
              className={`flex min-h-16 w-full items-center gap-3 rounded-md border px-3 py-3 text-left text-sm transition-colors ${
                isActive
                  ? "border-brand-ink bg-brand-ink text-white"
                  : isComplete
                    ? "border-teal bg-teal-soft text-teal"
                    : "border-brand-cloud bg-white text-brand-slate"
              }`}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-current text-xs font-semibold">
                {index + 1}
              </span>
              <span className="font-medium leading-5">{step.title}</span>
            </button>
          </li>
        );
      })}
    </ol>

    <section className="overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm">
      <div className="p-6 md:p-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="border-b border-brand-cloud pb-6">
              <p className="text-sm font-medium uppercase text-brand-slate">
                {editMode ? "Edit listing" : "New listing"}
              </p>
              <h2 className="mt-2 public-section-title">
                {currentStep.title}
              </h2>
              <p className="mt-2 text-sm leading-6 text-brand-slate">{currentStep.description}</p>
            </div>

            {currentStep.id === "service" && (
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Service name</FormLabel>
                      <FormControl>
                        <Input
                          data-testid="name"
                          placeholder="e.g. John's Cleaning Services"
                          {...field}
                          disabled={isDisabled}
                        />
                      </FormControl>
                      <FormDescription>What is the name of your service or business?</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          data-testid="description"
                          placeholder="Describe your service in detail"
                          className="min-h-[140px]"
                          {...field}
                          disabled={isDisabled}
                        />
                      </FormControl>
                      <FormDescription>Help customers understand what you offer and who you help.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="website_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website or social profile</FormLabel>
                      <FormControl>
                        <Input data-testid="website_url" placeholder="e.g. example.com" {...field} disabled={isDisabled} />
                      </FormControl>
                      <FormDescription>Optional. We will add https:// if you enter a domain.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {currentStep.id === "visibility" && (
              <div className="space-y-8">
                <BasicInfoSection
                  form={form}
                  isDisabled={isDisabled}
                  categories={categories}
                  selectedCategories={selectedCategories}
                  handleCategorySelect={handleCategorySelect}
                  handleRemoveCategory={handleRemoveCategory}
                  isPremium={isPremium}
                  isAdmin={isAdmin}
                  isWhatsAppTier={isWhatsAppTier}
                />

                <LocationSection
                  form={form}
                  isPremium={isPremium}
                  isAdmin={isAdmin}
                  isDisabled={isDisabled}
                  areas={Object.values(areasGrouped).flat().map(area => {
                    // Convert string areas to AreaResult type if needed
                    if (typeof area === 'string') {
                      return {
                        id: area,
                        name: area,
                        territory: '',
                        created_at: null
                      } as AreaResult;
                    }
                    return area as AreaResult;
                  })}
                  areasGrouped={Object.entries(areasGrouped).reduce((acc, [territory, areas]) => {
                    acc[territory] = areas.map(area => {
                      if (typeof area === 'string') {
                        return {
                          id: area,
                          name: area,
                          territory: territory,
                          created_at: null
                        } as AreaResult;
                      }
                      return area as AreaResult;
                    });
                    return acc;
                  }, {} as Record<string, AreaResult[]>)}
                  selectedSuburbs={selectedSuburbs}
                  handleSuburbSelect={handleSuburbSelect}
                  handleSuburbBulkSelect={handleSuburbBulkSelect}
                  handleRemoveSuburb={handleRemoveSuburb}
                  handleSuburbBulkRemove={handleSuburbBulkRemove}
                  isWhatsAppVerified={isWhatsAppVerified}
                />
              </div>
            )}

            {currentStep.id === "image" && (
              <MediaUploadSection
                isDisabled={isDisabled}
                handleImageChange={handleImageChange}
                imageError={imageError}
                fileName={fileName}
                imagePreview={imagePreview}
                useDefaultBanner={useDefaultBanner}
                onUseDefaultBannerChange={handleUseDefaultBannerChange}
              />
            )}

            {isReviewStep && (
              <div className="space-y-6">
                <Alert className="rounded-md border-amber bg-amber-soft">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-brand-slate">
                    {finalReviewNotice}
                  </AlertDescription>
                </Alert>

                <div className="overflow-hidden rounded-md border border-brand-cloud">
                  {reviewImageSrc ? (
                    <img
                      src={reviewImageSrc}
                      alt="Listing preview"
                      className="h-48 w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="flex h-48 items-center justify-center bg-brand-mist text-sm text-brand-slate">
                      No image selected
                    </div>
                  )}

                  <div className="p-5">
                    <h3 className="public-section-title">
                      {watchedValues.name || "Untitled listing"}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-brand-slate">
                      {watchedValues.description || "No description added yet."}
                    </p>
                  </div>

                  <dl className="divide-y divide-brand-cloud border-t border-brand-cloud">
                    <div className="grid gap-2 p-4 sm:grid-cols-[12rem_1fr]">
                      <dt className="text-sm font-medium text-brand-slate">Contact</dt>
                      <dd className="text-sm text-brand-ink">{watchedValues.website_url || "Not provided"}</dd>
                    </div>
                    <div className="grid gap-2 p-4 sm:grid-cols-[12rem_1fr]">
                      <dt className="text-sm font-medium text-brand-slate">Categories</dt>
                      <dd className="text-sm text-brand-ink">{selectedCategories.length > 0 ? selectedCategories.join(", ") : "None selected"}</dd>
                    </div>
                    <div className="grid gap-2 p-4 sm:grid-cols-[12rem_1fr]">
                      <dt className="text-sm font-medium text-brand-slate">Areas served</dt>
                      <dd className="text-sm text-brand-ink">{areaLabels.length > 0 ? areaLabels.join(", ") : "None selected"}</dd>
                    </div>
                  </dl>
                </div>

                <TermsSection
                  form={form}
                  isDisabled={isDisabled}
                  termsError={termsError}
                />
              </div>
            )}

            {!isReviewStep ? (
              <div className="flex flex-col gap-3 border-t border-brand-cloud pt-6 sm:flex-row sm:justify-between">
                <div>
                  {canGoBack && (
                    <Button type="button" variant="outline" onClick={handlePreviousStep}>
                      Back
                    </Button>
                  )}
                </div>
                <Button type="button" onClick={() => void handleNextStep()} disabled={isDisabled}>
                  Continue
                </Button>
              </div>
            ) : (
              <FormActions
                isDisabled={isDisabled}
                isSubmitting={isSubmitting}
                editMode={editMode}
                onCancel={onCancel}
                onPrevious={handlePreviousStep}
              />
            )}
          </form>
        </Form>
      </div>
    </section>
  </div>;
};

export default ListingForm;
